import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getFilterValue, serializeFilters, deserializeFilters, stripRemovedFilterParams } from '../utils/urlBuilder';

/**
 * useUrlState - useState with automatic URL synchronization
 *
 * Works like useState but persists value to URL query parameters.
 * Supports browser back/forward, page refresh, and URL sharing.
 *
 * @param {string} key - URL parameter key (camelCase)
 * @param {*} defaultValue - Default value if not in URL
 * @returns {[value, setValue]} - Same interface as useState
 *
 * @example
 * // Boolean filter
 * const [p95Latency, setP95Latency] = useUrlState('p95Latency', false);
 *
 * // String filter
 * const [agent, setAgent] = useUrlState('agent', '');
 *
 * // Array filter (comma-separated in URL)
 * const [agents, setAgents] = useUrlState('agent', []);
 */
export function useUrlState(key, defaultValue = null) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial value from URL or use default
  const urlValue = getFilterValue(searchParams, key, defaultValue);
  const [value, setValue] = useState(urlValue);

  // Ensure default value is written to URL on mount if missing
  useEffect(() => {
    const currentValue = searchParams.get(key);

    // If URL doesn't have the param and we have a non-null default, write it
    if (!currentValue && defaultValue !== null && defaultValue !== '' && defaultValue !== false) {
      const currentFilters = deserializeFilters(searchParams);
      currentFilters[key] = defaultValue;
      const queryString = serializeFilters(currentFilters);
      setSearchParams(queryString, { replace: true });
    }
  }, []); // Run once on mount

  // Sync local state when URL changes (browser back/forward)
  useEffect(() => {
    const newUrlValue = getFilterValue(searchParams, key, defaultValue);
    setValue(newUrlValue);
  }, [searchParams, key, defaultValue]);

  // Update URL when value changes
  const setValueAndUrl = useCallback((newValue) => {
    setValue(newValue);

    // Update URL
    const currentFilters = deserializeFilters(searchParams);

    if (newValue === null || newValue === undefined || newValue === '' || newValue === false || (Array.isArray(newValue) && newValue.length === 0)) {
      // Remove from URL if empty/false
      delete currentFilters[key];
    } else {
      // Add/update in URL
      currentFilters[key] = newValue;
    }

    const queryString = serializeFilters(currentFilters);
    setSearchParams(queryString, { replace: true }); // Use replace to avoid polluting browser history
  }, [key, searchParams, setSearchParams]);

  return [value, setValueAndUrl];
}

/**
 * useUrlFilters - Manage multiple URL-synced filters as an object
 *
 * Like useUrlState but handles multiple filters in a single object.
 * Useful when managing many related filters together.
 *
 * @param {Object} defaultFilters - Default filter values
 * @returns {[filters, setFilters, updateFilter, clearFilter]} - Filter state and updaters
 *
 * @example
 * const [filters, setFilters, updateFilter, clearFilter] = useUrlFilters({
 *   agent: '',
 *   p95Latency: false,
 *   errorRate: false
 * });
 *
 * // Update single filter
 * updateFilter('agent', 'trader');
 *
 * // Update multiple filters
 * setFilters({ agent: 'trader', p95Latency: true });
 *
 * // Clear single filter
 * clearFilter('agent');
 */
export function useUrlFilters(defaultFilters = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from URL or defaults
  const urlFilters = deserializeFilters(searchParams);
  const initialFilters = { ...defaultFilters, ...urlFilters };
  const [filters, setFiltersState] = useState(initialFilters);

  // Sync local state when URL changes (browser back/forward)
  useEffect(() => {
    const strippedParams = stripRemovedFilterParams(searchParams);
    if (strippedParams) {
      setSearchParams(strippedParams, { replace: true });
      return;
    }
    const newUrlFilters = deserializeFilters(searchParams);
    const mergedFilters = { ...defaultFilters, ...newUrlFilters };
    setFiltersState(mergedFilters);
  }, [searchParams, setSearchParams]); // Note: intentionally not including defaultFilters to avoid infinite loop

  // Update filters and URL
  const setFilters = useCallback((updates) => {
    const newFilters = typeof updates === 'function' ? updates(filters) : updates;

    setFiltersState(newFilters);

    // Update URL
    const queryString = serializeFilters(newFilters);
    setSearchParams(queryString, { replace: true });
  }, [filters, setSearchParams]);

  // Update single filter
  const updateFilter = useCallback((key, value) => {
    setFilters({ ...filters, [key]: value });
  }, [filters, setFilters]);

  // Clear single filter (set to default)
  const clearFilter = useCallback((key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
  }, [filters, setFilters]);

  return [filters, setFilters, updateFilter, clearFilter];
}

export default useUrlState;
